import { Card, Group, Image, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconPhotoCheck } from '@tabler/icons-react';
import { APP_ROKU_DELIVERABLE_SLOTS } from '@/lib/constants/appFactory';
import { useFactoryAssetSets } from '../useAppFactory';

interface AppRokuCurrentImagesProps {
  appCfgId: number;
}

/**
 * The LIVE deliverable set (the serving location builds and devices load
 * from) — console-era apps included, which is why this reads the factory's
 * currentImages instead of any dashboard-side upload state. Degrades to
 * nothing when the factory gateway is off (matching the Brand Kit card).
 */
export function AppRokuCurrentImages({ appCfgId }: AppRokuCurrentImagesProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId);
  if (assetSetsQuery.isError) return null;

  const current = assetSetsQuery.data?.currentImages ?? [];
  if (current.length === 0) return null;

  const labelByName = new Map(APP_ROKU_DELIVERABLE_SLOTS.map((s) => [s.name, s.label]));

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconPhotoCheck size={20} />
          <Text fw={600} size="md">
            Current Images
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          The images your Roku app and store builds use today. Publish a Brand Kit or an
          uploaded set below to replace them.
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {current.map((img) => (
            <Stack key={img.name} gap={4}>
              <Image src={img.url} alt={img.name} radius="sm" h={80} fit="contain" bg="gray.1" />
              <Text size="xs" c="dimmed" ta="center" truncate>
                {labelByName.get(img.name) ?? img.name}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
}
